import React, { useEffect, useState } from 'react';
import {
  Badge,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import * as Icon from 'react-feather';
import { useDispatch } from 'react-redux';

import CallexLoader from '../../../components/CallexLoader';
import { botCdrsTranscribeGet } from '../../../store/slices/tenantSlices/dailyReportSlice';

const sentimentColor = (value) => {
  const v = String(value || '').toLowerCase();
  if (['positive', 'good', 'happy', 'satisfied'].includes(v)) return 'success';
  if (['negative', 'poor', 'angry', 'frustrated'].includes(v)) return 'danger';
  if (['average', 'neutral', 'mixed'].includes(v)) return 'warning';
  return 'secondary';
};

const BotCdrTranscriptModal = ({ isOpen, onClose, sessionId, archiveId, sentiment }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('transcript');

  const fetchTranscript = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(botCdrsTranscribeGet({ sessionId, archiveId })).unwrap();
      setMessages(
        Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []
      );
    } catch (error) {
      console.log('error', error);
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && sessionId) {
      setActiveTab('transcript');
      fetchTranscript();
    }
    if (!isOpen) {
      setMessages([]);
    }
    // eslint-disable-next-line
  }, [isOpen, sessionId]);

  // Sentiment is provided by the CDR row (row.sentiment_analyse), not the transcribe call.
  const disposition = sentiment?.disposition_final;
  const details = sentiment?.sentiment_details || {};
  const highlights = Array.isArray(sentiment?.highlight_points) ? sentiment.highlight_points : [];
  const extracted = Array.isArray(sentiment?.extracted_data)
    ? sentiment.extracted_data.filter((item) => item && (item.value ?? '') !== '')
    : [];
  const highlightKeyword =
    typeof sentiment?.highlight_keyword === 'string' ? sentiment.highlight_keyword.trim() : '';
  const confidencePct = Math.round((disposition?.confidence ?? 0) * 1000) / 10;
  const score = typeof details.sentiment_score === 'number' ? details.sentiment_score : null;
  const scoreLeft = score != null ? ((Math.max(-1, Math.min(1, score)) + 1) / 2) * 100 : 50;

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" centered>
      <ModalHeader toggle={onClose}>Call Transcript</ModalHeader>
      <ModalBody className="call-details-modal">
        <Nav tabs className="inner-tabs call-details-tab mb-3">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === 'transcript' })}
              onClick={() => setActiveTab('transcript')}>
              Transcript
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === 'sentiment' })}
              onClick={() => setActiveTab('sentiment')}>
              Sentiment
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          {/* ===== Transcript ===== */}
          <TabPane tabId="transcript">
            {isLoading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: '40vh' }}>
                <CallexLoader />
              </div>
            ) : messages.length ? (
              <div className="chat-wrapper">
                {messages.map((msg, index) => {
                  const bot = msg.type === 'bot';
                  return (
                    <div key={index} className={`chat-row ${bot ? 'assistant' : 'user'}`}>
                      {bot && (
                        <div className="avatar assistant">
                          <Icon.Headphones size={16} />
                        </div>
                      )}
                      <div className="message-group">
                        <div className="chat-bubble">{msg.message}</div>
                        {msg.date_and_time && <div className="chat-time">{msg.date_and_time}</div>}
                      </div>
                      {!bot && (
                        <div className="avatar user">
                          <Icon.User size={16} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-3 text-center">No transcript available</div>
            )}
          </TabPane>

          {/* ===== Sentiment ===== */}
          <TabPane tabId="sentiment">
            {!sentiment ? (
              <div className="p-3 text-center text-muted">
                No sentiment data. Run “Analyse Sentiment” from the report first.
              </div>
            ) : (
              <div className="analysis-section">
                {disposition && (
                  <div className="analysis-card mb-3">
                    <div className="analysis-header d-flex justify-content-between align-items-center">
                      <span>Disposition</span>
                      <span>Confidence: {confidencePct}%</span>
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <Badge color={sentimentColor(disposition.value)} pill>
                        {disposition.value || '—'}
                      </Badge>
                      {disposition.conflict_flag && <Badge color="warning">Conflict</Badge>}
                    </div>
                    {disposition.reason && (
                      <div className="analysis-description mt-2">{disposition.reason}</div>
                    )}
                  </div>
                )}

                <div className="analysis-card mb-3">
                  <div className="analysis-header">Overall Sentiment</div>
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <Badge color={sentimentColor(details.overall_sentiment)} pill>
                      {details.overall_sentiment || '—'}
                    </Badge>
                    <span className="fw-bold">
                      {score != null ? `${score > 0 ? '+' : ''}${score.toFixed(2)}` : '—'}
                    </span>
                  </div>
                  <div
                    style={{
                      position: 'relative',
                      height: 8,
                      borderRadius: 999,
                      marginTop: 12,
                      background:
                        'linear-gradient(to right, rgba(244,67,54,0.55), rgba(255,178,43,0.55), rgba(44,208,126,0.55))'
                    }}>
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: `${scoreLeft}%`,
                        transform: 'translate(-50%, -50%)',
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        background: '#3e5569',
                        border: '2px solid #fff'
                      }}
                    />
                  </div>
                  <div className="variables-grid mt-3">
                    <div className="variable-item">
                      <div className="variable-label">Customer Emotion</div>
                      <div className="variable-value">{details.customer_emotion || '-'}</div>
                    </div>
                    <div className="variable-item">
                      <div className="variable-label">Bot Performance</div>
                      <div className="variable-value">{details.bot_performance || '-'}</div>
                    </div>
                    <div className="variable-item">
                      <div className="variable-label">Intent</div>
                      <div className="variable-value">{details.intent || '-'}</div>
                    </div>
                    <div className="variable-item">
                      <div className="variable-label">Turns Analysed</div>
                      <div className="variable-value">
                        {details.sentiment_timeline?.length ?? 0}
                      </div>
                    </div>
                  </div>
                </div>

                {(extracted.length > 0 || highlightKeyword) && (
                  <div className="analysis-card mb-3">
                    <div className="analysis-header">Extracted Details</div>
                    {highlightKeyword && (
                      <div className="analysis-description mt-2 p-2 rounded border border-info d-flex align-items-start gap-2">
                        <Icon.Star size={14} className="text-info mt-1 flex-shrink-0" />
                        <span>{highlightKeyword}</span>
                      </div>
                    )}
                    {extracted.length > 0 && (
                      <div className="variables-grid mt-3">
                        {extracted.map((item, i) => (
                          <div className="variable-item" key={i}>
                            <div className="variable-label">{item.label || '-'}</div>
                            <div className="variable-value">{item.value || '-'}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {highlights.length > 0 && (
                  <div className="analysis-card">
                    <div className="analysis-header">Highlights</div>
                    <div className="mt-2 d-flex flex-column gap-2">
                      {highlights.map((h, i) => {
                        const risk = String(h.type || '').toLowerCase() === 'risk';
                        return (
                          <div
                            key={i}
                            className={`p-2 rounded border ${risk ? 'border-danger' : 'border-success'}`}>
                            <div
                              className={`fw-bold small ${risk ? 'text-danger' : 'text-success'}`}>
                              {h.type || (risk ? 'Risk' : 'Positive')}
                            </div>
                            <div className="small">{h.text}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabPane>
        </TabContent>
      </ModalBody>
    </Modal>
  );
};

BotCdrTranscriptModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  sessionId: PropTypes.string,
  archiveId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sentiment: PropTypes.object
};

export default BotCdrTranscriptModal;
